import React from "react"

import { FileUp, Loader2, X } from "lucide-react"

import DocumentPreview from "@/components/application/DocumentPreview"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"

interface InsuranceSectionProps {
  insuranceProvider: string
  setInsuranceProvider: (value: string) => void
  policyNumber: string
  setPolicyNumber: (value: string) => void
  insuranceProofFile: File | null
  setInsuranceProofFile: (file: File | null) => void
  insuranceProofPreview: string | null
  setInsuranceProofPreview: (preview: string | null) => void
  onSubmit: () => void
  isLoading?: boolean
}

const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  insuranceProvider,
  setInsuranceProvider,
  policyNumber,
  setPolicyNumber,
  insuranceProofFile,
  setInsuranceProofFile,
  insuranceProofPreview,
  setInsuranceProofPreview,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast()

  const handleInsuranceProofChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setInsuranceProofFile(file)
      const fileUrl = URL.createObjectURL(file)
      setInsuranceProofPreview(fileUrl)
    }
  }

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement
    if (input) {
      input.click()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!insuranceProvider || !policyNumber || !insuranceProofFile) {
      toast({
        title: "Missing Information",
        description:
          "Please provide your insurance provider, policy number, and upload proof of insurance",
        variant: "destructive",
      })
      return
    }

    onSubmit()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Insurance Check</CardTitle>
        <CardDescription>
          Please provide your insurance details and upload proof of insurance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="insuranceProvider">Insurance Provider</Label>
            <Input
              id="insuranceProvider"
              value={insuranceProvider}
              onChange={(e) => setInsuranceProvider(e.target.value)}
              placeholder="Enter your insurance provider name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="policyNumber">Policy Number</Label>
            <Input
              id="policyNumber"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Enter your policy number"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="insuranceProof">Proof of Insurance</Label>
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 p-6 transition-colors hover:border-gray-400"
              onClick={() => triggerFileInput("insuranceProof")}
            >
              {insuranceProofPreview ? (
                <div className="flex flex-col items-center space-y-4">
                  <DocumentPreview
                    src={insuranceProofPreview}
                    alt="Insurance proof preview"
                    file={insuranceProofFile}
                  />
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setInsuranceProofFile(null)
                        setInsuranceProofPreview(null)
                      }}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <FileUp className="mb-4 h-12 w-12 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    Click to upload insurance certificate
                  </p>
                  <p className="text-xs text-gray-400">PNG, JPG or PDF (max. 10MB)</p>
                </>
              )}
            </div>
            <Input
              id="insuranceProof"
              type="file"
              className="hidden"
              onChange={handleInsuranceProofChange}
              accept="image/png,image/jpeg,application/pdf"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb text-white hover:bg-amber-500"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Insurance Information"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default InsuranceSection
